import React, { useCallback, useEffect, useState } from 'react';
import { Wallet, Calendar } from 'lucide-react';
import { apiClient } from '../../services/api';
import { API_BASE_URL } from '../../config';
import { hoyEnMexico } from '../../utils/dateFormat';

/**
 * Cuánto hay que pagarle hoy a cada closer.
 *
 * Iván (13-ago-2026): «que en mi dashboard pueda ver cuánto le tengo que pagar
 * de comisión diaria a cada closer».
 *
 * Va por día y no acumulado porque así se paga: el escalón se calcula sobre las
 * ventas **de esa jornada**, y un total del mes no responde la pregunta que se
 * hace quien va a sacar el dinero de la caja hoy.
 */
const pesos = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 2 })}`;

export default function ComisionesPorPagar() {
  // El día de pago es el día de México: con `toISOString()` a partir de las 6
  // de la tarde ya se abría en el día siguiente, que todavía no tiene ventas.
  const [fecha, setFecha] = useState(() => hoyEnMexico());
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async (dia) => {
    setCargando(true);
    try {
      const r = await apiClient.get(`${API_BASE_URL}/api/admin/closers/commissions`, { fecha: dia });
      setDatos(r?.data ?? null);
    } catch (e) {
      setDatos(null);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar(fecha);
  }, [fecha, cargar]);

  return (
    <div className="bg-white border border-vd-linea rounded-card shadow-warm-sm p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-grad-vivo flex items-center justify-center">
            <Wallet className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-display text-vd-h3 text-vd-tinta">Comisiones por pagar</h3>
            <p className="text-vd-pizarra text-sm">Lo que se le debe a cada closer por el día elegido</p>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-vd-grafito">
          <Calendar className="w-4 h-4 text-vd-pizarra" />
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="border border-vd-linea rounded-input px-3 py-2 text-vd-tinta"
          />
        </label>
      </div>

      {cargando ? (
        <p className="text-vd-pizarra text-sm">Consultando…</p>
      ) : !datos || datos.closers.length === 0 ? (
        <p className="text-vd-pizarra text-sm">Ese día no hubo activaciones, así que no hay nada que pagar.</p>
      ) : (
        <>
          <div className="rounded-card bg-vd-niebla border border-vd-linea p-4 mb-4 flex items-baseline justify-between">
            <span className="text-vd-grafito text-sm">
              {datos.activaciones_del_dia} activaciones · {datos.closers.length} closer
              {datos.closers.length === 1 ? '' : 's'}
            </span>
            <span className="font-display text-vd-h2 text-vd-tinta">{pesos(datos.total_a_pagar)}</span>
          </div>

          <div className="divide-y divide-vd-linea">
            {datos.closers.map((c) => (
              <div key={c.id} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-vd-tinta font-medium truncate">{c.nombre}</p>
                  <p className="text-vd-pizarra text-xs">
                    {c.codigo ? `${c.codigo} · ` : ''}
                    {c.activaciones} {c.activaciones === 1 ? 'venta' : 'ventas'}
                  </p>
                </div>
                <span className="font-display text-vd-h3 text-vd-tinta shrink-0">{pesos(c.comision)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
